<template>
  <div ref="root" class="hour-minute-picker">
    <div class="input-group">
      <div v-if="showIcon" class="input-group-prepend">
        <span class="input-group-text hour-minute-picker__prepend">
          <b-icon icon="clock"></b-icon>
        </span>
      </div>
      <div class="hour-minute-picker__input-wrapper">
      <input
        ref="input"
        :value="inputValue"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        class="form-control hour-minute-picker__control"
        :class="controlClasses"
        :disabled="disabled"
        :placeholder="placeholder"
        :aria-expanded="isOpen ? 'true' : 'false'"
        @input="handleTextInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.down.prevent="openDropdown"
        @keydown.enter.prevent="commitInputValue"
        @keydown.esc.prevent="closeDropdown"
      />
      <button
        v-if="allowClear && normalizedValue && !disabled"
        type="button"
        class="hour-minute-picker__clear"
        aria-label="Effacer l'heure"
        @click.stop="clearValue"
      >
        ×
      </button>
      </div>
    </div>

    <div v-if="isOpen" class="hour-minute-picker__menu shadow" :style="menuStyle">
      <div class="hour-minute-picker__columns">
        <div class="hour-minute-picker__column">
          <div class="hour-minute-picker__column-title">Heure</div>
          <button
            v-for="hour in hourOptions"
            :key="`hour-${hour}`"
            type="button"
            class="hour-minute-picker__option"
            :class="{ 'is-selected': hour === selectedHour }"
            @click="selectHour(hour)"
          >
            {{ pad(hour) }}
          </button>
        </div>

        <div class="hour-minute-picker__column">
          <div class="hour-minute-picker__column-title">Minute</div>
          <button
            v-for="minute in minuteOptions"
            :key="`minute-${minute}`"
            type="button"
            class="hour-minute-picker__option"
            :class="{ 'is-selected': minute === selectedMinute }"
            @click="selectMinute(minute)"
          >
            {{ pad(minute) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HourMinutePicker",
  model: {
    prop: "value",
    event: "input"
  },
  props: {
    value: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    allowClear: {
      type: Boolean,
      default: false
    },
    minuteStep: {
      type: Number,
      default: 1
    },
    placeholder: {
      type: String,
      default: "Choisir une heure"
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: "default"
    },
    valueFormat: {
      type: String,
      default: "HH:mm:ss"
    },
    displayFormat: {
      type: String,
      default: "HH:mm"
    }
  },
  data() {
    return {
      isOpen: false,
      inputValue: "",
      draftHour: null,
      draftMinute: null,
      menuStyle: {}
    };
  },
  computed: {
    normalizedValue() {
      if (!this.value) return null;
      const match = String(this.value).match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
      if (!match) return null;
      return {
        hour: Number(match[1]),
        minute: Number(match[2]),
        second: Number(match[3] || "00")
      };
    },
    displayValue() {
      if (!this.normalizedValue) return "";
      return `${this.pad(this.normalizedValue.hour)}:${this.pad(this.normalizedValue.minute)}`;
    },
    selectedHour() {
      if (this.draftHour !== null) return this.draftHour;
      return this.normalizedValue ? this.normalizedValue.hour : null;
    },
    selectedMinute() {
      if (this.draftMinute !== null) return this.draftMinute;
      return this.normalizedValue ? this.normalizedValue.minute : null;
    },
    hourOptions() {
      return Array.from({ length: 24 }, (_, index) => index);
    },
    minuteOptions() {
      const step = Math.max(1, Number(this.minuteStep) || 1);
      const options = [];
      for (let minute = 0; minute < 60; minute += step) {
        options.push(minute);
      }
      return options;
    },
    controlClasses() {
      return {
        "form-control-sm": this.size === "sm",
        "form-control-lg": this.size === "lg",
        "hour-minute-picker__control--open": this.isOpen,
        "hour-minute-picker__control--disabled": this.disabled
      };
    }
  },
  mounted() {
    this.syncInputValue();
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleDocumentClick);
  },
  watch: {
    value() {
      this.syncInputValue();
    }
  },
  methods: {
    pad(value) {
      return String(value).padStart(2, "0");
    },
    buildValue(hour, minute) {
      const normalizedHour = this.pad(hour);
      const normalizedMinute = this.pad(minute);
      if (this.valueFormat === "HH:mm") {
        return `${normalizedHour}:${normalizedMinute}`;
      }
      return `${normalizedHour}:${normalizedMinute}:00`;
    },
    emitValue(nextValue) {
      this.$emit("input", nextValue);
      this.$emit("change", nextValue);
    },
    syncInputValue() {
      this.inputValue = this.displayValue;
    },
    sanitizeTextValue(value) {
      const digits = String(value || "").replace(/\D/g, "").slice(0, 4);
      if (digits.length <= 2) return digits;
      return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    },
    parseTextValue(value) {
      const sanitized = this.sanitizeTextValue(value);
      const parts = sanitized.split(":");
      if (parts.length !== 2 || parts[0].length !== 2 || parts[1].length !== 2) {
        return null;
      }

      const hour = Number(parts[0]);
      const minute = Number(parts[1]);

      if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
      if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

      return { hour, minute };
    },
    clearValue() {
      this.draftHour = null;
      this.draftMinute = null;
      this.inputValue = "";
      this.emitValue(null);
      this.closeDropdown();
    },
    openDropdown() {
      if (this.disabled || this.isOpen) return;
      this.isOpen = true;
      this.draftHour = this.normalizedValue ? this.normalizedValue.hour : null;
      this.draftMinute = this.normalizedValue ? this.normalizedValue.minute : null;
      this.$nextTick(() => {
        const rect = this.$refs.input && this.$refs.input.getBoundingClientRect();
        if (rect) {
          this.menuStyle = {
            position: "fixed",
            top: `${rect.bottom + 4}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`
          };
        }
      });
      this.$emit("openChange", true);
    },
    closeDropdown() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.$emit("openChange", false);
    },
    toggleDropdown() {
      if (this.disabled) return;
      if (this.isOpen) {
        this.closeDropdown();
        return;
      }
      this.openDropdown();
    },
    selectHour(hour) {
      this.draftHour = hour;
      const minute = this.selectedMinute !== null ? this.selectedMinute : 0;
      const nextValue = this.buildValue(hour, minute);
      this.inputValue = `${this.pad(hour)}:${this.pad(minute)}`;
      this.emitValue(nextValue);
    },
    selectMinute(minute) {
      this.draftMinute = minute;
      const hour = this.selectedHour !== null ? this.selectedHour : 0;
      const nextValue = this.buildValue(hour, minute);
      this.inputValue = `${this.pad(hour)}:${this.pad(minute)}`;
      this.emitValue(nextValue);
      this.closeDropdown();
    },
    handleTextInput(event) {
      const nextInputValue = this.sanitizeTextValue(event.target.value);
      this.inputValue = nextInputValue;
      if (event.target.value !== nextInputValue) {
        event.target.value = nextInputValue;
      }

      const parsed = this.parseTextValue(nextInputValue);
      if (!parsed) return;

      this.draftHour = parsed.hour;
      this.draftMinute = parsed.minute;
      this.emitValue(this.buildValue(parsed.hour, parsed.minute));
    },
    commitInputValue() {
      const parsed = this.parseTextValue(this.inputValue);
      if (!parsed) {
        this.syncInputValue();
        return;
      }

      this.draftHour = parsed.hour;
      this.draftMinute = parsed.minute;
      this.inputValue = `${this.pad(parsed.hour)}:${this.pad(parsed.minute)}`;
      const nextValue = this.buildValue(parsed.hour, parsed.minute);
      if (nextValue !== this.value) {
        this.emitValue(nextValue);
      }
    },
    handleDocumentClick(event) {
      if (!this.$refs.root) return;
      if (this.$refs.root.contains(event.target)) return;
      this.commitInputValue();
      this.closeDropdown();
    },
    handleFocus(event) {
      if (!this.disabled) {
        this.openDropdown();
      }
      this.$emit("focus", event);
    },
    handleBlur(event) {
      this.commitInputValue();
      this.$emit("blur", event);
    }
  }
};
</script>

<style scoped>
.hour-minute-picker {
  position: relative;
  width: 100%;
}

.hour-minute-picker__input-wrapper {
  position: relative;
  flex: 1 1 auto;
  width: 1%;
  min-width: 0;
}

.hour-minute-picker__control {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  padding-right: 2.25rem;
}

@media (max-width: 768px) {
  .hour-minute-picker__prepend {
    display: none !important;
  }

  .hour-minute-picker__control {
    border-top-left-radius: 0.375rem;
    border-bottom-left-radius: 0.375rem;
  }
}

.hour-minute-picker__control--disabled {
  cursor: not-allowed;
}

.hour-minute-picker__control--open {
  border-color: var(--color-stroke-regular);
}

.hour-minute-picker__prepend {
  min-width: 2.75rem;
  justify-content: center;
}

.hour-minute-picker__clear {
  position: absolute;
  top: 50%;
  right: 0.75rem;
  transform: translateY(-50%);
  color: var(--color-secondary);
}

.hour-minute-picker__clear {
  border: 0;
  background: transparent;
  padding: 0;
  line-height: 1;
  font-size: 1rem;
}

.hour-minute-picker__menu {
  z-index: 1100;
  min-width: 220px;
  border: 1px solid var(--color-border-light);
  border-radius: 0.375rem;
  background: var(--color-surface-elevated);
}

.hour-minute-picker__columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.hour-minute-picker__column {
  max-height: 220px;
  overflow-y: auto;
  padding: 0.5rem;
}

.hour-minute-picker__column + .hour-minute-picker__column {
  border-left: 1px solid var(--color-border-light);
}

.hour-minute-picker__column-title {
  position: sticky;
  top: -0.5rem;
  padding: 0.5rem 0;
  margin-bottom: 0.25rem;
  background: var(--color-surface-elevated);
  color: var(--color-secondary);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.hour-minute-picker__option {
  width: 100%;
  border: 0;
  border-radius: 0.25rem;
  background: transparent;
  color: var(--color-text-primary);
  padding: 0.35rem 0.5rem;
  text-align: left;
}

.hour-minute-picker__option:hover {
  background: var(--color-surface-light);
}

.hour-minute-picker__option.is-selected {
  background: var(--color-primary);
  color: var(--color-primary-text);
}
</style>
